import { Form, Button } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSection from 'components/CIMSSection'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useForm } from 'antd/lib/form/Form'
import { useEffect, useState } from 'react'
import { DevelopersFormItems } from './types'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { EditUserInput, useEditDevelopersMutation } from 'schema'
import { useAuthContext } from 'context/AuthContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { dateFormatter } from 'utils/dateFormatter'
import { useDevelopersModuleContext } from '../../../../DevelopersModuleContext'

const Developers: React.VFC = () => {
  const { user } = useAuthContext()
  const { developers, users, loading, refetch } = useDevelopersModuleContext()
  const [form] = useForm<DevelopersFormItems>()
  const [edit] = useEditDevelopersMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editUser: {
        user: { username },
      },
    }) => simpleEditNotification(username),
  })
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    form.resetFields()
  }, [form, developers])

  const initialValues: DevelopersFormItems = {
    developers: toLabeledValues(developers, {
      label: 'username',
      value: 'id',
      tooltip: ({ developerDate, developerGrantedBy }) =>
        `Granted by ${developerGrantedBy?.username} on ${dateFormatter.datetime(
          developerDate
        )}`,
    }),
  }
  return (
    <CIMSSection
      loading={loading}
      title="Developers"
      help="Users with full access to the entire application"
    >
      <Form
        form={form}
        onFinish={async (values) => {
          /**
           * assuming we're always demoting developers at the start
           */
          const upserts = developers.map<
            Pick<EditUserInput, 'id' | 'developer'>
          >(({ id }) => ({ id, developer: false }))
          /**
           * if we find a developer in the values, remove them from upserts
           *
           * otherwise, add value to upserts as a developer
           */
          values.developers.forEach(({ value }) => {
            const foundIndex = upserts.findIndex(({ id }) => id === value)
            if (foundIndex === -1)
              upserts.push({ id: value.toString(), developer: true })
            else upserts.splice(foundIndex, 1)
          })
          if (!upserts.length) return
          setEditing(true)
          await Promise.all(
            upserts.map((input) => edit({ variables: { input } }))
          )
          setEditing(false)
          refetch()
        }}
        initialValues={initialValues}
      >
        <CIMSFormItem label="Users" name="developers">
          <CIMSSelect
            loading={loading}
            options={useOptions({
              items: users,
              sort: 'username',
              value: 'id',
              label: 'username',
              /**
               * you shouldn't be able to demote yourself... we need at least one developer
               */
              disabled: ({ id }) => id === user?.id,
              tooltip: ({ mail, username }) =>
                username === mail ? undefined : mail,
            })}
            /**
             * you shouldn't be able to demote yourself... we need at least one developer
             */
            allowClear={false}
            mode="multiple"
          />
        </CIMSFormItem>
        <Button
          loading={editing}
          disabled={editing}
          type="primary"
          htmlType="submit"
        >
          Save
        </Button>
      </Form>
    </CIMSSection>
  )
}

export default Developers
